import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { CampoUtenteDef, TipoCampoUtente } from '../../_models/CampoUtenteDef';

interface RigaRender {
  def: CampoUtenteDef;
  valore: string;
  storico: boolean;          // true → valore storico riferito a definizione disattivata, read-only
  valoriAmmessi?: string[];  // pre-parsed per i campi Lista
  invalid?: string;          // messaggio di errore di validazione client-side
  valoreEsolver?: string;    // valore del campo in ESOLVER (hint read-only, feature 105)
}

@Component({
  selector: 'app-campi-utente-form',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './campi-utente-form.component.html',
  styleUrls: ['./campi-utente-form.component.scss'],
})
export class CampiUtenteFormComponent implements OnChanges {
  /** Definizioni attive renderizzate come input editabili. */
  @Input() definizioni: CampoUtenteDef[] = [];
  /** Definizioni disattivate ma con valore presente sul record: rese in sola lettura (US4). */
  @Input() definizioniStoriche: CampoUtenteDef[] = [];
  /** Valore corrente come stringa JSON serializzata (es. `'{"vasca":"B"}'`) oppure null. */
  @Input() value: string | null = null;
  /** Disabilita interamente il form (es. record in sola lettura). */
  @Input() readonly = false;
  /**
   * Valori dei campi utente presenti in ESOLVER, keyed per codice ESOLVER (trimmed).
   * Le righe la cui definizione ha `codiceEsolver` corrispondente mostrano un hint read-only
   * "ESOLVER: <valore>" sotto il campo (feature 105). Assente → rendering invariato.
   */
  @Input() valoriEsolver: Record<string, string> | null = null;

  // FR-007a: la serializzazione avviene solo al save dell'ordine via il metodo pubblico `serialize()`,
  // invocato dal parent tramite @ViewChild. Niente più @Output valueChange su ogni keystroke.

  // Esposto al template per il switch sul tipo.
  readonly TipoCampoUtente = TipoCampoUtente;

  righe: RigaRender[] = [];

  ngOnChanges(_changes: SimpleChanges): void {
    this.rebuild();
  }

  private rebuild() {
    const parsed: Record<string, string> = {};
    if (this.value) {
      try {
        const obj = JSON.parse(this.value) as Record<string, unknown>;
        for (const [k, v] of Object.entries(obj ?? {})) {
          if (v === null || v === undefined) continue;
          parsed[k] = typeof v === 'string' ? v : String(v);
        }
      } catch {
        // value malformato → trattato come vuoto.
      }
    }

    const righe: RigaRender[] = [];

    // 1) Definizioni attive (sempre renderizzate, anche se non valorizzate).
    const attiveSorted = (this.definizioni ?? []).slice().sort((a, b) => a.ordinamento - b.ordinamento);
    for (const def of attiveSorted) {
      righe.push({
        def,
        valore: parsed[def.codice] ?? '',
        storico: false,
        valoriAmmessi: this.parseValoriAmmessi(def),
        valoreEsolver: this.valoreEsolverFor(def),
      });
    }

    // 2) Definizioni storiche per cui esiste un valore salvato: lette in sola lettura (US4).
    const codiciAttivi = new Set(attiveSorted.map(d => d.codice));
    for (const def of (this.definizioniStoriche ?? [])) {
      if (codiciAttivi.has(def.codice)) continue;
      const v = parsed[def.codice];
      if (v === undefined || v === '') continue;
      righe.push({
        def,
        valore: v,
        storico: true,
        valoriAmmessi: this.parseValoriAmmessi(def),
        valoreEsolver: this.valoreEsolverFor(def),
      });
    }

    this.righe = righe;
  }

  private valoreEsolverFor(def: CampoUtenteDef): string | undefined {
    const codice = def.codiceEsolver?.trim();
    if (!codice || !this.valoriEsolver) return undefined;
    return this.valoriEsolver[codice];
  }

  private parseValoriAmmessi(def: CampoUtenteDef): string[] | undefined {
    if (def.tipoCampo !== TipoCampoUtente.Lista || !def.valoriAmmessiJson) return undefined;
    try {
      const arr = JSON.parse(def.valoriAmmessiJson) as string[];
      return Array.isArray(arr) ? arr : undefined;
    } catch {
      return undefined;
    }
  }

  onValueChanged(riga: RigaRender, raw: unknown) {
    if (riga.storico) return; // safety: storici read-only

    let stringa = '';
    if (raw === true)      stringa = 'true';
    else if (raw === false) stringa = 'false';
    else if (raw === null || raw === undefined) stringa = '';
    else stringa = String(raw);

    riga.valore = stringa;
    riga.invalid = this.validateRiga(riga);

    // FR-007a: nessuna emissione al parent qui — serialize() viene invocato dal parent al save.
  }

  /**
   * Serializza i valori correnti dei campi utente nel formato JSON destinato a `OrdineVendita.CampiUtente`.
   * Da invocare dal componente parent (es. `ordine-vendita-create`) tramite `@ViewChild`
   * immediatamente prima del salvataggio dell'ordine (FR-007a).
   *
   * Restituisce `null` se nessun valore valido è presente.
   * Omette: valori vuoti, valori non validi (`riga.invalid`) e i valori storici (definizioni soft-deleted).
   */
  serialize(): string | null {
    const out: Record<string, string> = {};
    for (const r of this.righe) {
      if (r.storico) continue;            // non ri-trasmettere valori storici (vedi US4)
      if (!r.valore) continue;             // omette campi vuoti
      if (r.invalid) continue;             // non emette valori palesemente non validi
      out[r.def.codice] = r.valore;
    }
    return Object.keys(out).length === 0 ? null : JSON.stringify(out);
  }

  private validateRiga(riga: RigaRender): string | undefined {
    if (!riga.valore) return undefined; // valori vuoti ammessi (facoltativi)
    switch (riga.def.tipoCampo) {
      case TipoCampoUtente.Numero: {
        const n = Number(riga.valore);
        if (!Number.isFinite(n)) return 'Valore numerico non valido.';
        return undefined;
      }
      case TipoCampoUtente.Booleano:
        return ['true','false'].includes(riga.valore.toLowerCase()) ? undefined : 'Atteso sì/no.';
      case TipoCampoUtente.Lista:
        return (riga.valoriAmmessi ?? []).includes(riga.valore)
          ? undefined
          : `Valore non ammesso. Validi: ${(riga.valoriAmmessi ?? []).join(', ')}.`;
      default:
        return undefined;
    }
  }

}
